import { Sunrise, Sunset } from "lucide-react";

interface SunArcProps {
  sunrise: number;
  sunset: number;
  current: number;
}

export function SunArc({ sunrise, sunset, current }: SunArcProps) {
  const dayLength = sunset - sunrise;
  const progress = Math.max(0, Math.min(1, (current - sunrise) / dayLength));
  const isDayTime = current >= sunrise && current <= sunset;

  // Arc parameters
  const cx = 100;
  const cy = 80;
  const r = 70;
  const startAngle = Math.PI; // 180deg (left)
  const endAngle = 0; // 0deg (right)

  // Sun position on arc
  const angle = startAngle - progress * Math.PI;
  const sunX = cx + r * Math.cos(angle);
  const sunY = cy - r * Math.sin(angle);

  const formatTime = (ts: number) =>
    new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <div className="rounded-2xl border border-border bg-card p-4 overflow-hidden">
      <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
        <Sunrise className="h-4 w-4 text-amber-400" />
        Sun Path
      </h3>
      <svg viewBox="0 0 200 100" className="w-full h-auto">
        <defs>
          <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="oklch(0.7 0.15 30)" stopOpacity="0.6" />
            <stop offset="50%" stopColor="oklch(0.85 0.18 80)" stopOpacity="0.9" />
            <stop offset="100%" stopColor="oklch(0.65 0.15 30)" stopOpacity="0.6" />
          </linearGradient>
          <radialGradient id="sunGlow">
            <stop offset="0%" stopColor="oklch(0.95 0.18 85)" stopOpacity="1" />
            <stop offset="50%" stopColor="oklch(0.85 0.2 75)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="oklch(0.85 0.2 75)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Horizon line */}
        <line x1="10" y1="80" x2="190" y2="80" stroke="oklch(1 0 0 / 0.1)" strokeDasharray="2 4" />

        {/* Sun arc */}
        <path
          d={`M 30 80 A 70 70 0 0 1 170 80`}
          stroke="url(#arcGrad)"
          strokeWidth="2"
          fill="none"
          strokeDasharray="3 3"
          opacity="0.5"
        />

        {/* Filled progress arc */}
        {isDayTime && (
          <path
            d={`M 30 80 A 70 70 0 0 1 ${sunX} ${sunY}`}
            stroke="url(#arcGrad)"
            strokeWidth="2.5"
            fill="none"
          />
        )}

        {/* Sunrise marker */}
        <circle cx="30" cy="80" r="3" fill="oklch(0.65 0.15 30)" />
        <text x="30" y="95" textAnchor="middle" className="text-[8px] fill-muted-foreground">
          {formatTime(sunrise)}
        </text>

        {/* Sunset marker */}
        <circle cx="170" cy="80" r="3" fill="oklch(0.6 0.18 25)" />
        <text x="170" y="95" textAnchor="middle" className="text-[8px] fill-muted-foreground">
          {formatTime(sunset)}
        </text>

        {/* Sun position (only if daytime) */}
        {isDayTime && (
          <>
            <circle cx={sunX} cy={sunY} r="14" fill="url(#sunGlow)" className="glow-pulse" />
            <circle cx={sunX} cy={sunY} r="5" fill="oklch(0.95 0.18 85)" />
          </>
        )}
      </svg>

      <div className="mt-2 flex justify-between text-xs">
        <div className="flex items-center gap-1 text-muted-foreground">
          <Sunrise className="h-3 w-3 text-amber-400" />
          <span>Sunrise</span>
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <Sunset className="h-3 w-3 text-orange-400" />
          <span>Sunset</span>
        </div>
      </div>
    </div>
  );
}
